// frontend/src/pages/EmployerList.jsx
// Updated with contact_status display and quick status update
import React from "react";

const EmployerList = ({
  employers,
  loading,
  onEdit,
  onDelete,
  onStatusChange,
  contactStatuses = [],
  searchTerm,
  onCreate,
}) => {
  const formatAddress = (employer) => {
    const parts = [];
    if (employer.city) parts.push(employer.city);
    if (employer.state_code) parts.push(employer.state_code);
    return parts.join(", ") || "-";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusObj = contactStatuses.find((s) => s.value === status) || {
      label: status || "New",
      color: "secondary",
    };
    return (
      <span className={`badge bg-${statusObj.color}`}>{statusObj.label}</span>
    );
  };

  const handleDelete = (employer) => {
    if (
      window.confirm(
        `Are you sure you want to delete ${employer.company_name}?`
      )
    ) {
      onDelete(employer);
    }
  };

  return (
    <div className="card">
      <div className="card-body p-2 p-md-3">
        {loading ? (
          <div className="text-center py-3 py-md-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : employers.length === 0 ? (
          <div className="text-center py-3 py-md-5">
            <i
              className="bi bi-building text-muted"
              style={{ fontSize: "3rem" }}
            ></i>
            <h5 className="mt-3 text-muted">No Employers Found</h5>
            <p className="text-muted">
              {searchTerm
                ? "Try adjusting your search criteria"
                : "Add your first employer contact to get started"}
            </p>
            {!searchTerm && (
              <button className="btn btn-primary" onClick={onCreate}>
                <i className="bi bi-plus-circle me-2"></i>
                Add First Employer
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="table-responsive d-none d-lg-block">
              <table className="table table-hover table-sm align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Company Name</th>
                    <th>Contact</th>
                    <th>Phone</th>
                    <th>Location</th>
                    <th>Industry</th>
                    <th>Contact Status</th>
                    <th>Last Contact</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employers.map((employer) => (
                    <tr
                      key={employer.employer_id}
                      className={!employer.is_active ? "table-secondary" : ""}
                    >
                      <td>
                        <strong>{employer.company_name}</strong>
                        {employer.website && (
                          <a
                            href={
                              employer.website.startsWith("http")
                                ? employer.website
                                : `https://${employer.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ms-2"
                            title="Visit website"
                          >
                            <i className="bi bi-box-arrow-up-right"></i>
                          </a>
                        )}
                        {employer.total_contacts > 0 && (
                          <small className="text-muted d-block">
                            {employer.total_contacts} contact
                            {employer.total_contacts !== 1 ? "s" : ""} logged
                          </small>
                        )}
                      </td>
                      <td>
                        {employer.contact_name || "-"}
                        {employer.contact_title && (
                          <small className="text-muted d-block">
                            {employer.contact_title}
                          </small>
                        )}
                      </td>
                      <td>
                        {employer.phone ? (
                          <a href={`tel:${employer.phone}`}>{employer.phone}</a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{formatAddress(employer)}</td>
                      <td>{employer.industry || "-"}</td>
                      <td>
                        {/* Status dropdown for quick update */}
                        <div className="dropdown">
                          <button
                            className="btn btn-sm dropdown-toggle p-0 border-0"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            {getStatusBadge(employer.contact_status)}
                          </button>
                          <ul className="dropdown-menu">
                            {contactStatuses.map((status) => (
                              <li key={status.value}>
                                <button
                                  className={`dropdown-item ${
                                    employer.contact_status === status.value
                                      ? "active"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    onStatusChange(employer, status.value)
                                  }
                                >
                                  <span
                                    className={`badge bg-${status.color} me-2`}
                                  >
                                    {status.label}
                                  </span>
                                  <small className="text-muted">
                                    {status.description}
                                  </small>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </td>
                      <td>
                        {formatDate(employer.last_contact_date)}
                        {employer.next_followup_date && (
                          <small className="text-muted d-block">
                            Next: {formatDate(employer.next_followup_date)}
                          </small>
                        )}
                      </td>
                      <td className="text-end">
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => onEdit(employer)}
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(employer)}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="d-block d-lg-none">
              {employers.map((employer) => (
                <div
                  key={employer.employer_id}
                  className={`card mb-2 ${!employer.is_active ? "bg-light" : ""}`}
                >
                  <div className="card-body p-2">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="fw-bold text-truncate">
                          {employer.company_name}
                          {employer.website && (
                            <a
                              href={
                                employer.website.startsWith("http")
                                  ? employer.website
                                  : `https://${employer.website}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ms-2"
                            >
                              <i className="bi bi-box-arrow-up-right small"></i>
                            </a>
                          )}
                        </div>
                        <div className="small text-muted">
                          {employer.contact_name || "No contact"}
                          {employer.contact_title && ` - ${employer.contact_title}`}
                        </div>
                      </div>
                      <div className="ms-2">
                        {/* Status dropdown */}
                        <div className="dropdown">
                          <button
                            className="btn btn-sm dropdown-toggle p-0 border-0"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            {getStatusBadge(employer.contact_status)}
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            {contactStatuses.map((status) => (
                              <li key={status.value}>
                                <button
                                  className={`dropdown-item small ${
                                    employer.contact_status === status.value
                                      ? "active"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    onStatusChange(employer, status.value)
                                  }
                                >
                                  <span className={`badge bg-${status.color} me-1`}>
                                    {status.label}
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2 small text-muted mb-2">
                      {employer.phone && (
                        <a href={`tel:${employer.phone}`} className="text-decoration-none">
                          <i className="bi bi-telephone me-1"></i>
                          {employer.phone}
                        </a>
                      )}
                      <span>
                        <i className="bi bi-geo-alt me-1"></i>
                        {formatAddress(employer)}
                      </span>
                      {employer.industry && (
                        <span>
                          <i className="bi bi-briefcase me-1"></i>
                          {employer.industry}
                        </span>
                      )}
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        {employer.last_contact_date ? (
                          <>
                            <i className="bi bi-clock me-1"></i>
                            {formatDate(employer.last_contact_date)}
                          </>
                        ) : (
                          "No contact date"
                        )}
                        {employer.next_followup_date && (
                          <span className="ms-2 text-primary">
                            Next: {formatDate(employer.next_followup_date)}
                          </span>
                        )}
                      </small>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => onEdit(employer)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDelete(employer)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Summary */}
        {!loading && employers.length > 0 && (
          <div className="text-muted small mt-2 mt-md-3">
            Showing {employers.length} employer
            {employers.length !== 1 ? "s" : ""}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployerList;